import type {
	FleetCreateDamageRequestDto,
	FleetDamageDto,
	FleetDamageModelDataDto,
	FleetDamageStatusDto,
	MobiliDamagePointDto,
} from "../types/damage-3d.types";

// TODO: Delete this file once backend PR #586 merges and the fleet API
// client is regenerated. Replace the mock calls in vehicles.service.ts
// with the generated endpoint functions.
// Mirrors the endpoints defined on the `experimental/damage-report-3d`
// branch of be.battmobility.bff.

const DESIGN_NAME = "volvoEX30";

function seedData(vehicleId: string): FleetDamageModelDataDto {
	const points: Array<MobiliDamagePointDto> = [
		{
			id: "point-001",
			damageId: "damage-001",
			position: { x: -1.2, y: 0.5, z: -1.8 },
			normal: { x: 0, y: 0.5, z: -0.866 },
			designName: DESIGN_NAME,
			title: "Rear bumper dent",
			description: "Dent on rear bumper",
			imageUrl:
				"https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=400",
			comment: "Approx. 15 cm dent, no paint damage",
			phase: "AFTER_USAGE",
			status: "CREATED",
			creationDate: new Date(
				Date.now() - 2 * 24 * 60 * 60 * 1000
			).toISOString(),
		},
		{
			id: "point-002",
			damageId: "damage-002",
			position: { x: 0.8, y: 1.2, z: 0 },
			normal: { x: 0.8, y: 0, z: 0 },
			designName: DESIGN_NAME,
			title: "Passenger door scratch",
			description: "Surface scratch on passenger door",
			imageUrl:
				"https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=400",
			comment: "~20 cm long, paint exposed",
			phase: "DURING_USAGE",
			status: "CREATED",
			creationDate: new Date(
				Date.now() - 5 * 24 * 60 * 60 * 1000
			).toISOString(),
		},
		{
			id: "point-003",
			damageId: "damage-002",
			position: { x: 0.9, y: 1.0, z: 0.05 },
			normal: { x: 0.85, y: -0.1, z: 0.05 },
			designName: DESIGN_NAME,
			title: "Passenger door scratch (detail)",
			description: "Secondary scratch below main damage",
			imageUrl:
				"https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=400",
			comment: "Minor scratch, same repair job",
			phase: "DURING_USAGE",
			status: "CREATED",
			creationDate: new Date(
				Date.now() - 5 * 24 * 60 * 60 * 1000
			).toISOString(),
		},
		{
			id: "point-004",
			damageId: "damage-003",
			position: { x: 1.1, y: 1.5, z: 0.7 },
			normal: { x: 0.3, y: 0.5, z: 0.85 },
			designName: DESIGN_NAME,
			title: "Rear window crack",
			description: "Small crack on rear quarter panel window",
			imageUrl:
				"https://images.unsplash.com/photo-1487180144351-b8472da7d491?w=400",
			comment: "Repaired with replacement glass",
			phase: "BEFORE_USAGE",
			status: "FIXED",
			creationDate: new Date(
				Date.now() - 10 * 24 * 60 * 60 * 1000
			).toISOString(),
		},
	];

	return {
		vehicleId,
		vehicleName: "Demo vehicle",
		designName: DESIGN_NAME,
		// null triggers the in-scene placeholder car. Replace with a signed
		// GLB URL when the backend is live.
		modelFileUrl: null,
		damages: [
			{
				id: "damage-001",
				vehicleId,
				user: { id: "user-001", displayName: "Alex Martin" },
				description: "Dent on rear bumper",
				status: "CREATED",
				phase: "AFTER_USAGE",
				creationDate: new Date(
					Date.now() - 2 * 24 * 60 * 60 * 1000
				).toISOString(),
				imageUrls: [],
				damagePoints: [points[0]],
				designName: DESIGN_NAME,
			},
			{
				id: "damage-002",
				vehicleId,
				user: { id: "user-002", displayName: "Jamie Verhoeven" },
				description: "Scratch on passenger door",
				status: "CREATED",
				phase: "DURING_USAGE",
				creationDate: new Date(
					Date.now() - 5 * 24 * 60 * 60 * 1000
				).toISOString(),
				imageUrls: [],
				damagePoints: [points[1], points[2]],
				designName: DESIGN_NAME,
			},
			{
				id: "damage-003",
				vehicleId,
				user: { id: "user-003", displayName: "Sam Dubois" },
				description: "Minor window crack",
				status: "FIXED",
				phase: "BEFORE_USAGE",
				creationDate: new Date(
					Date.now() - 10 * 24 * 60 * 60 * 1000
				).toISOString(),
				imageUrls: [],
				damagePoints: [points[3]],
				designName: DESIGN_NAME,
			},
		],
		allDamagePoints: points,
	};
}

// Session-scoped store, keyed by vehicleId. Writes by the mocked service
// methods land here, so refetches reflect the changes made in this session.
//
// Map iteration order is insertion order, so we also use it as an LRU:
// touching a vehicle re-inserts it at the tail, and we evict from the
// head once `MAX_VEHICLES_IN_STORE` is exceeded. This keeps session
// memory bounded even if an admin cycles through many vehicles.
const MAX_VEHICLES_IN_STORE = 32;
const store = new Map<string, FleetDamageModelDataDto>();

// Release all Map references when the tab is closed or navigated away so
// the store doesn't pin memory for the remaining lifetime of the page.
if (typeof window !== "undefined") {
	window.addEventListener("pagehide", () => store.clear(), { once: true });
}

function touch(vehicleId: string, data: FleetDamageModelDataDto): void {
	// Re-insert to bump to the tail of insertion order.
	store.delete(vehicleId);
	store.set(vehicleId, data);
	while (store.size > MAX_VEHICLES_IN_STORE) {
		const oldest = store.keys().next();
		if (oldest.done) break;
		store.delete(oldest.value);
	}
}

function ensure(vehicleId: string): FleetDamageModelDataDto {
	const existing = store.get(vehicleId);
	if (existing) {
		touch(vehicleId, existing);
		return existing;
	}
	const data = seedData(vehicleId);
	touch(vehicleId, data);
	return data;
}

function clone(data: FleetDamageModelDataDto): FleetDamageModelDataDto {
	return structuredClone(data);
}

function generateId(prefix: string): string {
	return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

export function getMockDamage3DModelData(
	vehicleId: string
): FleetDamageModelDataDto {
	// Always hand back a clone so downstream code can't mutate the store
	// accidentally via references.
	return clone(ensure(vehicleId));
}

export function mockCreateDamage(
	vehicleId: string,
	body: FleetCreateDamageRequestDto
): FleetDamageDto {
	const data = ensure(vehicleId);
	const damageId = generateId("damage");
	const createdAt = new Date().toISOString();

	const damagePoints: Array<MobiliDamagePointDto> = body.damagePoints.map(
		(point, index) => ({
			id: generateId(`point-${index}`),
			damageId,
			position: point.position,
			normal: point.normal,
			designName: point.designName ?? body.designName ?? data.designName,
			title: point.title,
			description: point.description,
			imageUrl: point.imageUrl,
			comment: point.comment,
			phase: body.phase,
			status: "CREATED",
			creationDate: createdAt,
			bookingId: body.bookingId,
		})
	);

	const damage: FleetDamageDto = {
		id: damageId,
		vehicleId,
		user: { id: "user-self", displayName: "Fleet admin" },
		vehicleUsageId: body.vehicleUsageId,
		bookingId: body.bookingId,
		description: body.description,
		status: "CREATED",
		phase: body.phase,
		creationDate: createdAt,
		imageUrls: [],
		damagePoints,
		designName: body.designName ?? data.designName,
	};

	data.damages = [damage, ...data.damages];
	data.allDamagePoints = [...data.allDamagePoints, ...damagePoints];
	return damage;
}

export function mockDeleteDamage(vehicleId: string, damageId: string): void {
	const data = ensure(vehicleId);
	data.damages = data.damages.filter((damage) => damage.id !== damageId);
	data.allDamagePoints = data.allDamagePoints.filter(
		(point) => point.damageId !== damageId
	);
}

/**
 * Test-only helper — drops every cached vehicle so tests start from a
 * clean state. Not part of the production surface; importing it from
 * production code is a smell (there is no runtime caller outside tests).
 */
export function __resetMockDamage3DStore__(): void {
	store.clear();
}

export function mockToggleDamageStatus(
	vehicleId: string,
	damageId: string,
	status: FleetDamageStatusDto
): void {
	const data = ensure(vehicleId);
	const damage = data.damages.find((d) => d.id === damageId);
	if (!damage) return;
	damage.status = status;
	for (const point of damage.damagePoints ?? []) {
		point.status = status;
	}
}

// Full-replace update — matches the demo HTML's PUT /damages/{id} shape.
// The body contains the full list of damage points; the backend replaces
// the damage with what's sent.
export function mockUpdateDamage(
	vehicleId: string,
	damageId: string,
	body: FleetCreateDamageRequestDto
): FleetDamageDto {
	const data = ensure(vehicleId);
	const existing = data.damages.find((d) => d.id === damageId);
	if (!existing) {
		throw new Error(`Damage ${damageId} not found`);
	}

	const designName = body.designName ?? existing.designName ?? data.designName;
	const updatedPoints: Array<MobiliDamagePointDto> = body.damagePoints.map(
		(point, index) => ({
			id:
				point.id && !point.id.startsWith("new-")
					? point.id
					: generateId(`point-${index}`),
			damageId,
			position: point.position,
			normal: point.normal,
			designName: point.designName ?? designName,
			title: point.title,
			description: point.description,
			imageUrl: point.imageUrl,
			comment: point.comment,
			phase: body.phase,
			status: existing.status,
			creationDate: point.creationDate ?? existing.creationDate,
			bookingId: body.bookingId,
		})
	);

	existing.description = body.description;
	existing.phase = body.phase;
	existing.bookingId = body.bookingId;
	existing.vehicleUsageId = body.vehicleUsageId;
	existing.designName = designName;
	existing.damagePoints = updatedPoints;

	data.allDamagePoints = data.allDamagePoints
		.filter((point) => point.damageId !== damageId)
		.concat(updatedPoints);

	return existing;
}
